import { useState, useCallback, useMemo, memo, useEffect } from 'react'

import { ASC, DESC } from './constants'
import { Container, Wrapper, Controls, ActionButton } from './components'
import { Table } from './table'
import { Search } from './search'
import { Export } from './export'
import { Pagination } from './pagination'
import { SearchMode } from './searchMode'
import { Skeleton } from './skeleton'

const GridComponent = ({
  server,
  columns,
  options,
  actions,
  rowIdKey,
  onClickRow,
  onSelectRow,
  onClearSearch,
  onChangePageSize,
  defaultSortBy,
  isRowDisabled,
  NoSearchResultsComponent,
  defaultPage = 1,
  isPagerVisible = true,
  isRowSelectable = false,
  shouldReload = false,
  shouldClearSelected = false,
  dateRangePicker = false,
  defaultSortOrder = ASC,
  defaultPageSize = Pagination.lengths[0]
}) => {
  /**
   * Read the `server` prop in as lazy initial state so that it is only computed once on
   * page load. This way clients can pass the same `server` prop without triggering a fetch.
   * Mostly to guard against reference changes across renders when clients don't useMemo.
   *
   * @see https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
   */
  const [config, setConfig] = useState(() => server)
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [sortBy, setSortBy] = useState(defaultSortBy || columns[0].id)
  const [sortOrder, setSortOrder] = useState(defaultSortOrder)
  const [page, setPage] = useState(defaultPage)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [query, setQuery] = useState('')
  const [queryDateRange, setQueryDateRange] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRows, setSelectedRows] = useState([])
  const labeledColumns = useMemo(() => columns.filter(({ label }) => label.length), [
    columns
  ])
  const {
    csvExportName,
    isSearchQueryHighlighted,
    disableFilters,
    fullWidth = false
  } = options
  const onSort = useCallback(
    id => {
      if (id !== sortBy) {
        setSortOrder(ASC)
        setSortBy(id)
      } else {
        setSortOrder(sortOrder === ASC ? DESC : ASC)
      }
    },
    [sortBy, sortOrder]
  )
  const renderTable = () => {
    if (!isFetching) {
      if (data.length) {
        return (
          <Table
            data={data}
            columns={columns}
            query={searchTerm}
            rowIdKey={rowIdKey}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onClickRow={onClickRow}
            onSelectRow={onSelectRow}
            isRowDisabled={isRowDisabled}
            isSelectable={isRowSelectable}
            isSearchQueryHighlighted={isSearchQueryHighlighted}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            onSort={onSort}
          />
        )
      }

      if (!searchTerm && config.mode === 'search') {
        return <SearchMode text={config.searchModeText} />
      }

      if (query || queryDateRange.length) {
        const [startDate, endDate] = queryDateRange
        const { minStartDate, maxEndDate } = dateRangePicker
        const start = startDate || minStartDate
        const end = endDate || maxEndDate

        return (
          <NoSearchResultsComponent searchTerm={searchTerm} dateRange={[start, end]} />
        )
      }
    }

    return <Skeleton columns={labeledColumns.length} />
  }
  const onSearchChange = useCallback(
    value => {
      setQuery(value)

      if (!value) {
        setSearchTerm('')

        if (config.mode === 'search') {
          setData([])
          setTotal(0)
        }
      }
    },
    [config.mode]
  )
  const onSearchClear = useCallback(() => {
    const prevSearchTerm = searchTerm

    setQuery('')
    setSearchTerm('')

    if (config.mode === 'search') {
      setData([])
      setTotal(0)
    }

    onClearSearch(prevSearchTerm)
  }, [config.mode, searchTerm, onClearSearch])
  const onRangeClear = useCallback(() => {
    if (queryDateRange.length) {
      setQueryDateRange([])
    }
  }, [queryDateRange])
  const onSearchSubmit = useCallback(
    dateRange => {
      const [submitStart, submitEnd] = dateRange
      const [queryStart, queryEnd] = queryDateRange

      setPage(1)

      if (query !== searchTerm || submitStart !== queryStart || submitEnd !== queryEnd) {
        /**
         * Set isFetching early to avoid flicker of NoSearchResults when
         * server.mode === 'search'.
         */
        setIsFetching(true)
        setSearchTerm(query)
        setQueryDateRange(dateRange)
      }
    },
    [query, searchTerm, queryDateRange]
  )
  const handleOnChangePageSize = useCallback(
    size => {
      setPageSize(size)
      setPage(1)

      onChangePageSize(size)
    },
    [onChangePageSize]
  )

  useEffect(() => {
    const { mode } = config
    const controller = new AbortController()
    const handleFetchError = error => {
      // Update state before calling onError (which could remove Grid)
      setIsFetching(false)
      setData([])
      setTotal(0)

      if (config.onError) {
        config.onError(error)
      }
    }
    const onFetchSuccess = response => {
      const { format, onSuccess } = config
      const fetchedData = format ? format(response) : response

      // Update state before calling onSuccess (which could remove Grid)
      setIsFetching(false)
      setData(fetchedData)
      setTotal(config.total(response))

      if (onSuccess) {
        onSuccess(response, fetchedData, searchTerm, queryDateRange)
      }
    }
    const fetchData = async () => {
      const { url, pagination, sort, search, method } = config
      const request = config.fetch || window.fetch
      const opts = {
        credentials: 'omit',
        method: method || 'GET',
        signal: controller.signal
      }
      let params = `${pagination(page, pageSize)}&${sort(sortBy, sortOrder)}`
      let endpoint = url

      if (searchTerm || queryDateRange.length) {
        params += `&${search(searchTerm.trim(), queryDateRange)}`
      }

      endpoint += url.includes('?') ? `&${params}` : `?${params}`

      if (config.headers) {
        opts.headers = config.headers

        if (typeof config.headers === 'function') {
          opts.headers = config.headers()
        }
      }

      setIsFetching(true)

      try {
        const resp = await request(endpoint, opts)

        if (config.fetch) {
          onFetchSuccess(resp)
        } else if (resp.ok) {
          onFetchSuccess(await resp.json())
        } else {
          handleFetchError(
            new Error(`Fetching data failed: ${resp.status} - ${resp.statusText}`)
          )
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          handleFetchError(error)
        }
      }
    }

    if (mode === 'filter' || (mode === 'search' && searchTerm)) {
      fetchData()
    }

    return () => controller.abort()
  }, [page, pageSize, sortBy, sortOrder, searchTerm, config, queryDateRange])

  const onExportCsv = useCallback(() => {
    return config.export.csv({
      page,
      pageSize,
      sortBy,
      sortOrder,
      query: searchTerm,
      queryDateRange
    })
  }, [config.export, page, pageSize, sortBy, sortOrder, searchTerm, queryDateRange])

  useEffect(() => {
    if (shouldReload) {
      setConfig({ ...server })
    }
  }, [shouldReload, server])

  useEffect(() => {
    if (shouldClearSelected) {
      setSelectedRows([])
    }
  }, [shouldClearSelected])

  return (
    <Container>
      <Wrapper fullWidth={fullWidth} mode={config.mode}>
        {!disableFilters && (
          <Controls>
            <Search
              query={query}
              isDisabled={isFetching}
              onChange={onSearchChange}
              onClear={onSearchClear}
              onSubmit={onSearchSubmit}
              onRangeClear={onRangeClear}
              dateRangePicker={dateRangePicker}
            />
            {config.export && (
              <Export filename={csvExportName} onExportCsv={onExportCsv} />
            )}
            {Array.isArray(actions) &&
              actions.map(action => (
                <ActionButton
                  isDisabled={!selectedRows.length}
                  key={action.label}
                  isTextAlignLeft
                  size="small"
                  onClick={() => action.onAct({ selectedRows })}
                  icon={action.icon}>
                  {action.label}
                </ActionButton>
              ))}
          </Controls>
        )}
        {renderTable()}
      </Wrapper>
      {isPagerVisible && data.length ? (
        <Pagination
          page={page}
          pageSize={pageSize}
          isFetching={isFetching}
          total={total}
          onChangePage={setPage}
          onChangePageSize={handleOnChangePageSize}
        />
      ) : null}
    </Container>
  )
}
const Grid = memo(GridComponent)

export { Grid, GridComponent }
